import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useMatrix } from "~/context/MatrixContext";
import {
  getReceiptsForEvent,
  paginateThreadBackward,
  subscribeReceipts,
  summarizeThread,
  type Receipt,
  type ThreadSummary,
  type TimelineEvent,
} from "~/lib/matrix";

/**
 * Return value of the thread subscription hook. `replies` is the ordered
 * list of replies oldest → newest. `summary` is a memoised derivation
 * for the inline thread preview. `latestReplyId` is the id of the most
 * recent reply (or the root, when the thread is empty) — callers use
 * this for the `m.in_reply_to` fallback when sending a reply.
 *
 * `isLoading` is `true` until the first snapshot lands. The stub
 * delivers synchronously on subscribe so this flips on the same render
 * in tests; live sync may take a tick.
 *
 * Earlier-reply paging is opt-in: `canLoadEarlier` is optimistic while
 * the newest page is settled and no terminal page has been reported;
 * `loadEarlier` advances the per-thread cursor by exactly one page and
 * the merged window arrives through the same subscription callback.
 * `isLoadingEarlier` gates repeated clicks and `earlierError` carries a
 * recoverable retry signal.
 */
export interface UseThreadResult {
  replies: TimelineEvent[];
  summary: ThreadSummary;
  latestReplyId: string;
  isLoading: boolean;
  /** True while an explicit earlier-page fetch is in flight. */
  isLoadingEarlier: boolean;
  /** True after a failed earlier-page fetch; clears on the next attempt. */
  earlierError: boolean;
  /** Optimistic until the server reports a terminal page. */
  canLoadEarlier: boolean;
  /** Advance the per-thread cursor by one older page. No-op when a load
   *  is already running or none is possible. */
  loadEarlier: () => Promise<void>;
  receiptsByEventId: ReadonlyMap<string, Receipt[]>;
}

export const THREAD_PAGE_SIZE = 50;

/**
 * Subscribe to the replies of a thread rooted at `rootEventId` in
 * `roomId`. Returns the live reply list plus a derived summary used by
 * the inline preview.
 *
 * Pass an empty `rootEventId` or `roomId` to tear the subscription
 * down without unmounting the component (e.g. when closing the
 * `ThreadPanel`). The hook treats that case as "no thread selected"
 * and returns an empty result.
 *
 * `enabled` defaults to `true`; pass `false` to opt out of the
 * subscription entirely without rewiring the call site (e.g. an
 * inline preview that hasn't yet been told whether the message is a
 * thread root).
 */
export function useThread(
  roomId: string,
  rootEventId: string,
  enabled: boolean = true,
  includeReceipts: boolean = false,
): UseThreadResult {
  const { client, subscribeThread } = useMatrix();
  const [replies, setReplies] = useState<TimelineEvent[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isLoadingEarlier, setIsLoadingEarlier] = useState<boolean>(false);
  const [earlierError, setEarlierError] = useState<boolean>(false);
  // Terminal once the server reports an absent continuation. Missing
  // initially so `canLoadEarlier` stays optimistic after the newest page.
  const [atStart, setAtStart] = useState<boolean>(false);
  const [, setReceiptEpoch] = useState(0);

  // Bumped on every root/session change so a superseded earlier-page
  // result cannot rewrite newer state; the adapter also coalesces two
  // concurrent clicks onto one in-flight fetch.
  const generation = useRef(0);
  const inFlight = useRef<Promise<void> | null>(null);
  // Terminal once the server reports an absent continuation; a recoverable
  // error does not set it so the next attempt retries the same cursor.
  const noMore = useRef(false);

  useEffect(() => {
    if (!enabled || !roomId || !rootEventId) {
      setReplies([]);
      setIsLoading(false);
      setEarlierError(false);
      setAtStart(false);
      return;
    }
    // A new root or session is a fresh generation; `loadEarlier` ignores
    // any result that lands for a superseded subscription.
    generation.current += 1;
    inFlight.current = null;
    noMore.current = false;
    setAtStart(false);
    setEarlierError(false);
    setIsLoading(true);
    let active = true;
    const unsub = subscribeThread(roomId, rootEventId, (next, meta) => {
      if (!active) return;
      setReplies(next);
      setIsLoading(false);
      if (meta?.atStart) {
        setAtStart(true);
        noMore.current = true;
      }
    });
    return () => {
      active = false;
      unsub();
    };
  }, [enabled, roomId, rootEventId, subscribeThread]);

  useEffect(() => {
    if (!client || !enabled || !includeReceipts || !roomId || !rootEventId) return;
    return subscribeReceipts(client, roomId, () => setReceiptEpoch((epoch) => epoch + 1));
  }, [client, enabled, includeReceipts, roomId, rootEventId]);

  const loadEarlier = useCallback(async (): Promise<void> => {
    if (!enabled || !roomId || !rootEventId) return;
    // A terminal page means none is possible; do not fetch again.
    if (noMore.current) return;
    // Coalesce: join the in-flight load instead of stacking fetches.
    if (inFlight.current) {
      await inFlight.current;
      return;
    }
    if (!client) return;
    const gen = generation.current;
    const run = (async () => {
      setIsLoadingEarlier(true);
      setEarlierError(false);
      try {
        const result = await paginateThreadBackward(client, roomId, rootEventId, THREAD_PAGE_SIZE);
        if (generation.current === gen && result.atStart) {
          setAtStart(true);
          noMore.current = true;
        }
      } catch {
        // Retain the page and cursor on failure; the next attempt retries
        // the same cursor. Surface a recoverable error without losing
        // keyboard focus.
        if (generation.current === gen) setEarlierError(true);
      } finally {
        if (generation.current === gen) setIsLoadingEarlier(false);
      }
    })();
    inFlight.current = run;
    try {
      await run;
    } finally {
      if (inFlight.current === run) inFlight.current = null;
    }
  }, [client, enabled, roomId, rootEventId]);

  const summary = useMemo(() => summarizeThread(replies), [replies]);
  const latestReplyId = summary.lastReply?.id ?? rootEventId;
  const receiptsByEventId = new Map<string, Receipt[]>();
  if (client && includeReceipts) {
    for (const reply of replies) {
      const eventReceipts = getReceiptsForEvent(client, roomId, reply.id, rootEventId);
      if (eventReceipts.length > 0) receiptsByEventId.set(reply.id, eventReceipts);
    }
  }

  const canLoadEarlier = !isLoading && !isLoadingEarlier && !atStart && !earlierError;

  return {
    replies,
    summary,
    latestReplyId,
    isLoading,
    isLoadingEarlier,
    earlierError,
    canLoadEarlier,
    loadEarlier,
    receiptsByEventId,
  };
}
